// assets/b-track.js (#732): one tracker every shared block calls, so each block reports the same
// events with the same details on every store (Lee, 2026-10-27: "a scalable system because then each
// site that it turns on we can get analytics straight away").
//
//   bTrack(name, params, { legacyName, legacyParams })
//
// Each event goes two ways:
// - Shopify customer events (Shopify.analytics.publish). Shopify applies the shopper's consent before
//   any pixel sees it; the Haverford pixel (hub#1767) forwards it to the store's GA4 and Meta;
// - window.dataLayer, for the stores that load Tag Manager in their theme (Koenig, Hardware Box,
//   Catnets, Catnetting, Shade Matters, read 2026-10-27), only while the shopper allows analytics
//   (Shopify.customerPrivacy.analyticsProcessingAllowed). Before the consent API has loaded, events
//   wait for it, loaded the way Shopify documents (Shopify.loadFeatures, consent-tracking-api), as
//   Koenig's snippets/km-cta-tracking does. Denied, or no API at all: the data layer gets nothing.
// Every event carries `store` (the shop's domain). Callers pass product and page details only, never a
// shopper's. legacyName also sends the event under an older name with its own details (Koenig's
// km_compare_* events), so reports built on those keep counting until they move to the new names.
// A tracker failure never stops the page.

const store = (window.Shopify && window.Shopify.shop) || window.location.hostname;

export function eventPayload(params) {
  const payload = { store };
  for (const [key, value] of Object.entries(params || {})) {
    if (value !== undefined && value !== null && value !== '') payload[key] = value;
  }
  return payload;
}

const privacy = () => window.Shopify && window.Shopify.customerPrivacy;
const privacyReady = () => { const cp = privacy(); return !!(cp && typeof cp.analyticsProcessingAllowed === 'function'); };
const analyticsAllowed = () => { try { return privacyReady() && privacy().analyticsProcessingAllowed() === true; } catch (error) { return false; } };

function pushNow(name, payload) {
  if (!analyticsAllowed()) return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  } catch (error) {
    // A tag error never stops the page.
  }
}

let waiting = null;
function flush() {
  const queued = waiting || [];
  waiting = null;
  queued.forEach(([name, payload]) => pushNow(name, payload));
}
function pushToDataLayer(name, payload) {
  if (privacyReady()) return pushNow(name, payload);
  if (!waiting) {
    waiting = [];
    const loader = window.Shopify && window.Shopify.loadFeatures;
    if (typeof loader === 'function') {
      try { loader.call(window.Shopify, [{ name: 'consent-tracking-api', version: '0.1' }], flush); } catch (error) { setTimeout(flush, 0); }
    } else {
      setTimeout(flush, 0);
    }
  }
  if (waiting.length < 50) waiting.push([name, payload]);
}

function publish(name, payload) {
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, payload);
    }
  } catch (error) {
    // A pixel error never stops the page.
  }
  pushToDataLayer(name, payload);
}

export function bTrack(name, params, options) {
  if (!name) return;
  try {
    publish(name, eventPayload(params));
    const legacy = options && options.legacyName;
    // The older name goes to the data layer only, where the reports built on it live; pixels get the
    // standard name once.
    if (legacy && legacy !== name) pushToDataLayer(legacy, eventPayload(options.legacyParams || params));
  } catch (error) {
    // As above.
  }
}

window.bTrack = window.bTrack || bTrack;
